import { motion } from "framer-motion";
import React, { useState, useMemo } from "react";

import { StyledInput } from "@/styles/layout";
import { Body } from "@/styles/typography";

interface InputProps {
  name?: string | undefined;
  label: string;
  labelStyle?: any | undefined;
  containerStyle?: any | undefined;
  placeholder?: string | undefined;
  onChange?: (...i: any[]) => void;
  autoFocus?: boolean | undefined;
  type?: string | undefined;
  value?: string | number | undefined;
  error?: string | undefined;
  min?: number | undefined;
  max?: number | undefined;
  disabled?: boolean | undefined;
  autoComplete?: "on" | "off" | undefined;
}

const Input = ({
  name,
  label,
  containerStyle,
  placeholder,
  onChange,
  value,
  autoFocus,
  type = "text",
  min,
  max,
  error,
  disabled,
  autoComplete,
}: InputProps) => {
  const [isFocus, setIsFocus] = useState<boolean>(false);
  const [isHover, setIsHover] = useState<boolean>(false);

  const isTouched = useMemo(
    () => (value?.toString() ?? "").length > 0,
    [value]
  );

  const borderColor = useMemo(() => {
    if (error) {
      return "danger.border";
    } else if (isTouched) {
      return "primary.border";
    } else if (isFocus) {
      return "primary.border";
    } else if (isHover) {
      return "primary.borderHover";
    }
    return "primary.borderSubtle";
  }, [error, isTouched, isFocus, isHover]);

  return (
    <motion.div
      animate={{
        x: error ? [0, 5, -5, 0] : 0,
      }}
      transition={{ duration: 0.3 }}
      style={{
        width: "100%",
        ...containerStyle,
      }}
    >
      {label && (
        <motion.label
          animate={{
            fontSize: "12px",
            fontWeight: 600,
          }}
          style={{
            zIndex: 0,
            pointerEvents: "none",
          }}
        >
          <Body color={error ? "danger.textLow" : "primary.textHigh"} mb="4px">
            {error && !isFocus ? error : label.toUpperCase()}
          </Body>
        </motion.label>
      )}
      <StyledInput
        autoFocus={autoFocus}
        onFocus={() => setIsFocus(true)}
        onBlur={() => setIsFocus(false)}
        onMouseEnter={() => setIsHover(true)}
        onMouseLeave={() => setIsHover(false)}
        onChange={(e: any) => {
          onChange?.(e.target.value);
        }}
        bg={disabled ? "primary.element" : "primary.bgBase"}
        name={name}
        value={value}
        placeholder={placeholder}
        type={type}
        height="44px"
        width="100%"
        borderColor={borderColor}
        borderStyle="solid"
        borderWidth="1px"
        borderRadius="4px"
        px="16px"
        fontSize="16px"
        fontWeight="400"
        animate={{ paddingTop: isFocus || isTouched ? "0" : 0 }}
        min={min}
        max={max}
        style={{ pointerEvents: disabled ? "none" : "initial" }}
        autoComplete={autoComplete}
      />
    </motion.div>
  );
};

export default Input;
